/**
 * governance/cognitiveLevel.ts
 * Cognitive level detection for questions and explanations.
 *
 * Maps questions to Bloom's taxonomy levels (simplified 4-level model):
 * - recall:        Reproduce facts, definitions, procedures
 * - comprehension: Explain meaning, interpret, paraphrase
 * - application:   Apply formula/rule to a new situation, calculate
 * - analysis:      Compare, contrast, decompose, reason about structure
 */

import type { Question } from '../content';
export type CognitiveLevel = 'recall' | 'comprehension' | 'application' | 'analysis';

// ─── Detection patterns ───────────────────────────────────────────────────────

/** Analysis-level indicators: comparison, reasoning, decomposition, error analysis, reverse reasoning */
const ANALYSIS_PATTERNS = /vergelijk|verschil|waarom|analyseer|verklaar|redeneer|bewijs|leg uit waarom|welke conclusie|wat is de oorzaak|wat klopt er niet|wat is de fout|wat is zijn fout|welke stap mist|wat doet.{0,15}(?:fout|verkeerd)|corrigeer|beoordeel|reken\s*terug|terugrekenen|oorspronkelijke\s+(?:prijs|bedrag|waarde)|vóór\s+de\s+(?:korting|verhoging|actie|afprijzing)|waar\s+of\s+onwaar|juist\s+of\s+onjuist|in\s+de\s+juiste\s+volgorde/i;

/** Application-level indicators: calculation, formula use, step execution */
const APPLICATION_PATTERNS = /\d+\s*[×x\*÷\/\+\-]\s*\d+|=\s*\d|formule|bereken|stap|eerst.*dan|vervolgens|oplossen|uitrekenen|toepassen/i;

/** Comprehension-level indicators: meaning, explanation, interpretation */
const COMPREHENSION_PATTERNS = /betekent|want|omdat|uitleg|verklaring|bedoelt|beschrijf|wat is het verschil|hoe werkt|wat begrijp/i;

/** Recall-level indicators: name, list, define, identify */
const RECALL_PATTERNS = /wat is|welke|noem|geef|schrijf op|hoeveel|wanneer|wie|waar|definitie|omschrijf/i;

// ─── Main detection function ──────────────────────────────────────────────────

/**
 * Detects the cognitive level of a question based on its text and explanation.
 * Uses a priority cascade: analysis > application > comprehension > recall.
 */
export function detectCognitiveLevel(question: Question): CognitiveLevel {
  const questionText = question.question ?? '';
  const explanationText = question.feedback?.explanationText ?? '';
  const combined = `${questionText} ${explanationText}`.trim();

  // Priority cascade — highest level wins
  if (ANALYSIS_PATTERNS.test(combined)) return 'analysis';
  if (APPLICATION_PATTERNS.test(combined)) return 'application';
  if (COMPREHENSION_PATTERNS.test(combined)) return 'comprehension';

  // Default: recall (most basic level)
  return 'recall';
}

/**
 * Returns a human-readable label for a cognitive level.
 */
export function cognitiveLevelLabel(level: CognitiveLevel): string {
  const labels: Record<CognitiveLevel, string> = {
    recall: 'Recall',
    comprehension: 'Comprehension',
    application: 'Application',
    analysis: 'Analysis'
  };
  return labels[level];
}

/**
 * Returns a color class for a cognitive level (Tailwind).
 */
export function cognitiveLevelColor(level: CognitiveLevel): string {
  const colors: Record<CognitiveLevel, string> = {
    recall: 'text-slate-600 bg-slate-100',
    comprehension: 'text-blue-600 bg-blue-100',
    application: 'text-emerald-600 bg-emerald-100',
    analysis: 'text-purple-600 bg-purple-100'
  };
  return colors[level];
}

/**
 * Computes cognitive level distribution across a set of questions.
 */
export function computeCognitiveLevelDistribution(questions: Question[]): Record<CognitiveLevel, number> {
  const dist: Record<CognitiveLevel, number> = {
    recall: 0,
    comprehension: 0,
    application: 0,
    analysis: 0
  };
  questions.forEach(q => {
    dist[detectCognitiveLevel(q)]++;
  });
  return dist;
}